import { FileText, Tag, Check, QrCode } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { RadioGroup, RadioGroupItem } from '../../lib/shadcn/radio-group'
import { Label } from '../../lib/shadcn/label'
import { cn } from '../../lib/shadcn/utils'
import { usePicklistSize, type PicklistSize } from '../../utils/picklistSettings'
import { useToteLabelSize, type ToteLabelSize } from '../../utils/toteLabelSettings'

interface Option {
  value: PicklistSize
  title: string
  description: string
  icon: typeof FileText
}

interface ToteOption {
  value: ToteLabelSize
  title: string
  description: string
  icon: typeof FileText
}

const OPTIONS: Option[] = [
  {
    value: 'letter',
    title: 'US Letter (8.5 x 11 in)',
    description: 'Full-page picklist, ideal for printing on standard paper.',
    icon: FileText,
  },
  {
    value: 'label4x6',
    title: '4 x 6 in Label',
    description: 'Compact picklist formatted for thermal shipping label printers.',
    icon: Tag,
  },
]

const TOTE_OPTIONS: ToteOption[] = [
  {
    value: 'small',
    title: '2.25 x 1.25 in',
    description: 'Compact tote label for small thermal label printers.',
    icon: Tag,
  },
  {
    value: 'large',
    title: '4 x 2 in',
    description: 'Larger tote label with bigger QR code and text.',
    icon: QrCode,
  },
]

function OptionCard<T extends string>({
  opt,
  selected,
  idPrefix,
}: {
  opt: { value: T; title: string; description: string; icon: typeof FileText }
  selected: boolean
  idPrefix: string
}) {
  const Icon = opt.icon
  return (
    <Label
      htmlFor={`${idPrefix}-${opt.value}`}
      className={cn(
        'flex items-start gap-4 rounded-lg border p-4 cursor-pointer transition-colors',
        selected ? 'border-primary bg-accent' : 'border-border hover:bg-accent/50'
      )}
    >
      <RadioGroupItem value={opt.value} id={`${idPrefix}-${opt.value}`} className="mt-1" />
      <Icon className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{opt.title}</span>
          {selected && <Check className="w-4 h-4 text-primary" />}
        </div>
        <p className="text-sm text-muted-foreground font-normal">{opt.description}</p>
      </div>
    </Label>
  )
}

export default function PrintLabelSettings() {
  const [size, setSize] = usePicklistSize()
  const [toteSize, setToteSize] = useToteLabelSize()

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Default Picklist PDF Size</CardTitle>
          <CardDescription>
            Choose which PDF size is generated when you click the Picklist button on the Orders page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={size} onValueChange={v => setSize(v as PicklistSize)} className="gap-3">
            {OPTIONS.map(opt => (
              <OptionCard key={opt.value} opt={opt} selected={size === opt.value} idPrefix="picklist" />
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tote Label Size</CardTitle>
          <CardDescription>
            Choose the physical size of the tote labels generated from the Customers page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={toteSize} onValueChange={v => setToteSize(v as ToteLabelSize)} className="gap-3">
            {TOTE_OPTIONS.map(opt => (
              <OptionCard key={opt.value} opt={opt} selected={toteSize === opt.value} idPrefix="tote" />
            ))}
          </RadioGroup>
        </CardContent>
      </Card>
    </>
  )
}
